import { ChevronDown, Copy, Info, Landmark } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConfirmModal from '@/domains/dashboard-management/components/ConfirmModal/ConfirmModal';
import BottomAction from '@/domains/mobile-web/components/BottomAction/BottomAction';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import { useStoreAccounts } from '@/domains/mobile-web/hooks/useBanks';
import { useCancelOrder, useOrder, useReportTransfer } from '@/domains/mobile-web/hooks/useOrders';
import type { StoreAccount } from '@/domains/mobile-web/types/bank.types';
import type { TransferAccount } from '@/domains/mobile-web/types/order.types';
import { formatPaymentDeadline } from '@/domains/mobile-web/utils/order';
import { formatPrice } from '@/domains/mobile-web/utils/price';
import { cn } from '@/lib/utils';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import Seo from '@/shared/components/ui/Seo/Seo';

// Bank and e-wallet logos by their path under src/assets, as store_bank_accounts.logo_path holds it
const BANK_LOGOS = import.meta.glob<string>('/src/assets/store/icon/bank-*.svg', {
  eager: true,
  query: '?url',
  import: 'default',
});

/** Bundled logo URL; none for an unknown path, so the generic bank icon shows. */
const bankLogoUrl = (logoPath: string | null) =>
  (logoPath && BANK_LOGOS[`/src/assets/${logoPath}`]) || null;

const copyToClipboard = async (value: string) => {
  try {
    await navigator.clipboard?.writeText(value);
  } catch {}
};

const pad = (value: number) => String(value).padStart(2, '0');

/** hh:mm:ss until the deadline, 00:00:00 once it passed; ticks every second. */
const useRemaining = (deadline: string | null | undefined) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!deadline) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [deadline]);
  if (!deadline) return null;
  const seconds = Math.max(0, Math.floor((new Date(deadline).getTime() - now) / 1000));
  return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
};

/** A store account as Pembayaran shows it: one of the store's list, or the one already reported (an
 *  older payment's account has no id, short name or logo). */
interface BankChoice {
  id: string | null;
  name: string;
  holder: string;
  number: string;
  logo: string | null;
}

const fromStoreAccount = (account: StoreAccount): BankChoice => ({
  id: account.bank_account_id,
  name: account.short_name,
  holder: account.account_holder,
  number: account.account_number,
  logo: bankLogoUrl(account.logo_path),
});

const fromTransferAccount = (account: TransferAccount): BankChoice => ({
  id: account.bank_account_id,
  name: account.short_name ?? account.bank_name,
  holder: account.account_holder,
  number: account.account_number,
  logo: bankLogoUrl(account.logo_path),
});

const BankSummary = ({ choice }: { choice: BankChoice }) => (
  <>
    <figure className="flex h-[40px] w-[40px] shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-blue-cs-200 bg-white">
      {choice.logo ? (
        <img
          src={choice.logo}
          alt=""
          loading="eager"
          className="h-full w-full object-cover"
        />
      ) : (
        <Landmark size={20} className="text-text-grey" aria-hidden="true" />
      )}
    </figure>
    <div className="min-w-0 flex-1 text-left">
      <p className="body1bold truncate">{choice.name}</p>
      <p className="reg12 text-text-grey truncate">a/n {choice.holder}</p>
    </div>
  </>
);

interface BankPickerProps {
  options: BankChoice[];
  selected: BankChoice;
  /** Once reported, the account stays as reported */
  locked: boolean;
  onSelect: (choice: BankChoice) => void;
}

/** Rekening tujuan: the selected account, opening the store's list below it when there is a choice. */
const BankPicker = ({ options, selected, locked, onSelect }: BankPickerProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const canPick = !locked && options.length > 1;

  // Closes on a tap outside or Escape
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!canPick) {
    return (
      <div className="flex items-center gap-3 rounded-[12px] border border-blue-cs-200 p-3">
        <BankSummary choice={selected} />
      </div>
    );
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={listId}
        className="flex w-full items-center gap-3 rounded-[12px] border border-blue-cs-200 bg-white p-3 transition hover:bg-[#F3F8FF]"
      >
        <BankSummary choice={selected} />
        <ChevronDown
          size={20}
          className={cn('shrink-0 text-black transition-transform', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          id={listId}
          role="radiogroup"
          aria-label="Rekening tujuan"
          className="absolute inset-x-0 top-full z-20 mt-2 flex flex-col gap-1 rounded-[12px] border border-blue-cs-200 bg-white p-2 shadow-[0_4px_16px_rgba(0,0,0,0.12)]"
        >
          {options.map((option) => {
            const isSelected = option.id === selected.id;
            return (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => {
                  onSelect(option);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-3 rounded-[8px] p-2 transition',
                  isSelected ? 'bg-[#E8F1FF]' : 'hover:bg-[#F3F8FF]'
                )}
              >
                <BankSummary choice={option} />
                {isSelected && (
                  <span className="body2medium shrink-0 text-text-blue-100">Terpilih</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface CopyFieldProps {
  label: string;
  value: string;
  copyValue?: string;
  copied: boolean;
  onCopy: () => void;
}

const CopyField = ({ label, value, copyValue = value, copied, onCopy }: CopyFieldProps) => (
  <div className="flex flex-col gap-1">
    <p className="body2 text-text-grey">{label}</p>
    <div className="flex items-center justify-between gap-2 rounded-[8px] bg-[#F3F8FF] px-3 py-2">
      <span className="title2medium truncate">{value}</span>
      <button
        type="button"
        onClick={onCopy}
        aria-label={`Salin ${label.toLowerCase()} ${copyValue}`}
        className="flex shrink-0 items-center gap-1 body1medium text-text-blue-100 transition hover:opacity-80"
      >
        <Copy size={16} strokeWidth={1.8} />
        {copied ? 'Tersalin' : 'Salin'}
      </button>
    </div>
  </div>
);

/** Pembayaran (?id=<order id>): the store account picked and the exact amount to transfer, the last 3
 *  digits being the order's unique code. */
const Payment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('id') ?? undefined;
  const { data: order, isLoading, isError } = useOrder(orderId);
  const storeAccounts = useStoreAccounts();
  const reportTransfer = useReportTransfer();
  const cancelOrder = useCancelOrder();
  const [cancelling, setCancelling] = useState(false);
  const [copied, setCopied] = useState<'account' | 'amount' | null>(null);
  const [pickedId, setPickedId] = useState<string | null>(null);

  const payment = order?.payment;
  const remaining = useRemaining(payment?.expires_at);

  const handleCopy = (field: 'account' | 'amount', value: string) => {
    copyToClipboard(value);
    setCopied(field);
    window.setTimeout(() => setCopied((current) => (current === field ? null : current)), 1500);
  };

  const header = (
    <>
      <Seo title="Pembayaran" noindex />
      <HeaderMobile>
        <TopBarHeader type="back" onBack={() => navigate(-1)} title="Pembayaran" />
      </HeaderMobile>
    </>
  );

  const awaitingTransfer =
    order?.status === 'pending' &&
    payment?.status === 'pending' &&
    payment.provider === 'bank_transfer';
  if (!order || !payment || !awaitingTransfer) {
    const message = !orderId
      ? 'Pesanan tidak ditemukan.'
      : isLoading
        ? 'Memuat pembayaran...'
        : isError
          ? 'Gagal memuat pembayaran.'
          : order?.status === 'pending'
            ? 'Pesanan ini tidak dibayar dengan transfer bank.'
            : order
              ? `Pesanan ${order.order_number} tidak menunggu pembayaran (${order.status_label ?? order.status}).`
              : 'Pesanan tidak ditemukan.';
    return (
      <>
        {header}
        <WrapperMobile>
          <p className="body1 py-6 text-center text-text-grey">{message}</p>
          {order && (
            <button
              type="button"
              onClick={() => navigate('/store/history')}
              className="body1medium w-fit self-center text-text-blue-100 transition hover:opacity-80"
            >
              Lihat Riwayat Transaksi
            </button>
          )}
        </WrapperMobile>
      </>
    );
  }

  const amount = Number(payment.amount);
  const reported = payment.transfer_status === 'reported';
  const reportedAccount = payment.transfer_account
    ? fromTransferAccount(payment.transfer_account)
    : null;
  const options = (storeAccounts.data?.rows ?? []).map(fromStoreAccount);
  // Reported: the account reported. Otherwise the one picked here, else the one reported before a
  // rejection, else the store's first.
  const selected =
    reported && reportedAccount
      ? reportedAccount
      : (options.find((option) => option.id === (pickedId ?? reportedAccount?.id)) ??
        options[0] ??
        null);
  const canReport = !reported && Boolean(selected?.id) && !reportTransfer.isPending;

  const handleReport = () => {
    if (!canReport || !selected?.id) return;
    reportTransfer.mutate(
      { orderId: order.order_id, bankAccountId: selected.id },
      { onSuccess: () => navigate('/store/history') }
    );
  };

  const confirmCancel = () =>
    cancelOrder.mutate(order.order_id, {
      onSuccess: () => navigate('/store/history', { replace: true }),
      onSettled: () => setCancelling(false),
    });

  return (
    <>
      {header}

      <WrapperMobile className="pb-40">
        <div className="shadowCard inline-flex w-full flex-wrap justify-between gap-3 p-3">
          <p className="title2medium">No.Invoice</p>
          <p className="title2 text-text-grey truncate">{order.order_number}</p>
        </div>

        {payment.transfer_status === 'rejected' && (
          <div className="flex items-start gap-3 rounded-[12px] bg-[#FFE5E3] p-3">
            <Info size={20} className="mt-0.5 shrink-0 text-text-danger-500" aria-hidden="true" />
            <div className="flex flex-col gap-1">
              <p className="body2medium text-black">Transfer belum ditemukan</p>
              <p className="body2 text-text-grey">
                {payment.transfer_rejection_reason ??
                  'Admin belum menemukan transfer Anda. Periksa kembali nominal dan rekening tujuan.'}
              </p>
            </div>
          </div>
        )}

        <div className="shadowCard flex flex-col gap-3 p-3">
          {payment.expires_at && (
            <div className="flex items-center justify-between gap-3 flex-wrap-reverse">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <p className="title2medium">Batas Transfer</p>
                <p className="body2 text-text-grey">{formatPaymentDeadline(payment.expires_at)}</p>
              </div>
              <p className="title2medium text-text-danger-500">{remaining}</p>
            </div>
          )}

          {selected ? (
            <>
              <BankPicker
                options={options}
                selected={selected}
                locked={reported}
                onSelect={(choice) => setPickedId(choice.id)}
              />
              <CopyField
                label="No Rekening"
                value={selected.number}
                copied={copied === 'account'}
                onCopy={() => handleCopy('account', selected.number)}
              />
            </>
          ) : (
            <p className="body2 rounded-[12px] border border-blue-cs-200 p-3 text-text-grey">
              {storeAccounts.isLoading
                ? 'Memuat rekening tujuan...'
                : 'Rekening tujuan belum tersedia. Silakan coba lagi nanti.'}
            </p>
          )}
          <CopyField
            label="Jumlah Transfer"
            value={`Rp ${formatPrice(amount)}`}
            copyValue={String(amount)}
            copied={copied === 'amount'}
            onCopy={() => handleCopy('amount', String(amount))}
          />

          <div className="flex items-start gap-3 rounded-[12px] bg-[#FDE2CC] p-3">
            <Info size={20} className="mt-0.5 shrink-0 text-text-grey" aria-hidden="true" />
            <div className="flex flex-col gap-1">
              <p className="body2medium text-black">Pastikan nominal transfer sesuai</p>
              <p className="body2 text-text-grey">
                Transfer sesuai dengan total pembayaran yang tertera, termasuk kode unik
                {payment.unique_code ? ` (${payment.unique_code})` : ''} untuk kebutuhan konfirmasi
                pembayaran.
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setCancelling(true)}
          className="body1medium w-fit self-center text-text-danger-500 transition hover:opacity-80"
        >
          Batalkan Transaksi
        </button>
      </WrapperMobile>

      <BottomAction
        note={
          reported
            ? 'Transfer Anda sedang diverifikasi oleh admin. Pesanan diproses setelah pembayaran dikonfirmasi.'
            : 'Jika sudah melakukan transfer, klik tombol di bawah ini agar dapat dilanjut ke proses verifikasi.'
        }
        textButton={reported ? 'Menunggu Konfirmasi' : 'Saya sudah transfer'}
        disabled={!canReport}
        onClick={handleReport}
      />

      <ConfirmModal
        open={cancelling}
        title="Batalkan Pesanan ?"
        message="Apakah Anda yakin ingin membatalkan pesanan ini?"
        confirmLabel="Ya, Batalkan"
        variant="danger"
        loading={cancelOrder.isPending}
        onConfirm={confirmCancel}
        onClose={() => setCancelling(false)}
      />
    </>
  );
};

export default Payment;
